import { consumeAgUiStream } from '@/lib/agui/stream';
import type { RuntimeToolId, ToolSurface } from '@/lib/runtimeTools/manifest';
import type { RuntimeToolRunAgUiState } from '@/lib/runtimeTools/toolRuns';

import type {
  AgentRunNormalized,
  AgentRunResult,
  AgentRunState,
} from './types';

type RuntimeToolRunRequest = {
  toolId: RuntimeToolId;
  surface: ToolSurface;
  clientId?: string;
  brief?: string;
  input?: string;
  previousContent?: string;
  language?: 'en' | 'ar';
  locale?: 'en' | 'ar';
  timezone?: string;
  sourceConversationId?: string;
  artifactType?: string;
  sourceArtifactType?: string;
  outputArtifactType?: string;
  targetChannel?: string;
  targetLanguage?: string;
  artifactId?: string;
  sourceArtifactId?: string;
  parentArtifactId?: string;
  artifactKey?: string;
  lineageId?: string;
  plannerApproved?: boolean;
};

function normalizeTerminalState(state: RuntimeToolRunAgUiState | null): AgentRunResult {
  if (!state) {
    return { ok: false, error: 'tool stream ended without terminal state' };
  }

  if (state.status === 'failed') {
    return { ok: false, error: state.error || state.summary || 'tool run failed' };
  }

  const resultPayload = state.resultPayload || null;
  const rawState = (resultPayload?.rawState || {}) as AgentRunState;
  const normalized: AgentRunNormalized = {
    runId: state.runId,
    messageId: resultPayload?.messageId || resultPayload?.artifactId || state.createdArtifactIds[0],
    task: resultPayload?.task,
    route: resultPayload?.route,
    content: resultPayload?.content || '',
    rawState,
  };

  return {
    ok: true,
    data: {
      state: rawState,
      normalized,
      runtimeToolState: state,
    },
  };
}

export async function runRuntimeToolViaSSE(req: RuntimeToolRunRequest): Promise<AgentRunResult> {
  let authHeader: Record<string, string> = {};
  try {
    const { auth } = await import('@/lib/firebase');
    const maybeUser = (auth as { currentUser?: { getIdToken: () => Promise<string> } | null })?.currentUser;
    const token = maybeUser ? await maybeUser.getIdToken() : '';
    if (token) authHeader = { Authorization: `Bearer ${token}` };
  } catch {
    authHeader = {};
  }

  const response = await fetch('/api/agent-tools/run/live', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      ...authHeader,
    },
    body: JSON.stringify({
      clientId: req.clientId || 'default',
      toolId: req.toolId,
      surface: req.surface,
      ...(req.brief ? { brief: req.brief } : {}),
      ...(req.input ? { input: req.input } : {}),
      ...(req.previousContent ? { previousContent: req.previousContent } : {}),
      ...(req.language ? { language: req.language } : {}),
      ...(req.locale ? { locale: req.locale } : {}),
      ...(req.timezone ? { timezone: req.timezone } : {}),
      ...(req.sourceConversationId ? { sourceConversationId: req.sourceConversationId } : {}),
      ...(req.artifactType ? { artifactType: req.artifactType } : {}),
      ...(req.sourceArtifactType ? { sourceArtifactType: req.sourceArtifactType } : {}),
      ...(req.outputArtifactType ? { outputArtifactType: req.outputArtifactType } : {}),
      ...(req.targetChannel ? { targetChannel: req.targetChannel } : {}),
      ...(req.targetLanguage ? { targetLanguage: req.targetLanguage } : {}),
      ...(req.artifactId ? { artifactId: req.artifactId } : {}),
      ...(req.sourceArtifactId ? { sourceArtifactId: req.sourceArtifactId } : {}),
      ...(req.parentArtifactId ? { parentArtifactId: req.parentArtifactId } : {}),
      ...(req.artifactKey ? { artifactKey: req.artifactKey } : {}),
      ...(req.lineageId ? { lineageId: req.lineageId } : {}),
      ...(req.plannerApproved ? { plannerApproved: true } : {}),
    }),
  });

  if (!response.ok || !response.body) {
    const errorText = await response.text().catch(() => `/api/agent-tools/run/live ${response.status}`);
    return { ok: false, error: errorText || `/api/agent-tools/run/live ${response.status}` };
  }

  let latestState: RuntimeToolRunAgUiState | null = null;
  const { terminalType } = await consumeAgUiStream<RuntimeToolRunAgUiState>({
    stream: response.body,
    onEvent: (event) => {
      if (event.type === 'STATE_SNAPSHOT' && event.snapshot) {
        latestState = event.snapshot as RuntimeToolRunAgUiState;
        return;
      }
      if (event.type === 'RUN_ERROR') {
        latestState = latestState
          ? { ...latestState, status: 'failed', error: typeof event.message === 'string' ? event.message : latestState.error }
          : null;
      }
    },
  }).catch((error) => {
    return {
      terminalType: (error as Error)?.message ? 'error' as const : null,
    };
  });

  if (terminalType === 'error' && (!latestState || latestState.status !== 'completed')) {
    return {
      ok: false,
      error: latestState?.error || latestState?.summary || 'tool run failed',
    };
  }

  return normalizeTerminalState(latestState);
}
